import {
  Component,
  createSignal,
  Show,
  Switch,
  Match,
  For,
  createEffect,
} from "solid-js";
import { A, useParams, useNavigate } from "@solidjs/router";
import {
  Avatar,
  Badge,
  Button,
  Input,
  Modal,
  Select,
  Spinner,
} from "../components/ui";
import type { SelectOption } from "../components/ui";
import { RepoPicker } from "../components/github/RepoPicker";
import { RepositoryOverview } from "../components/github/RepositoryOverview";
import { StatusManager } from "../components/settings/StatusManager";
import { GuestLimitModal } from "../components/settings/GuestLimitModal";
import { useTooltips } from "../components/Tooltip";
import { useApiWithSource, useToast } from "../hooks";
import { currentUser } from "../stores/currentUserStore";
import { clearOnboardingInFlight } from "../stores/onboardingResume";
import {
  api,
  ApiError,
  getProject,
  updateProject,
  deleteProject,
  archiveProject,
  unarchiveProject,
  listMembers,
  addMember,
  removeMember,
  getProjectContext,
  updateProjectContext,
  sendInvite,
  listInvites,
  revokeInvite,
  changeTenantMemberRole,
  type ProjectId,
  type ProjectMember,
  type EstimateUnit,
  type QuotaLevel,
  type ProjectRole,
  type TenantRole,
  type TenantMembership,
  type PendingInvite,
} from "../api";
import {
  getUserRole,
  tenantRoleFor,
  canManageProject,
  canManageMembers,
  canInviteMembers,
  canDeleteProject,
  canLeaveProject,
  canManageOrgRoles,
} from "../utils/permissions";
import { useProjectQuota } from "../contexts/ProjectContext";

interface GitHubStatus {
  configured: boolean;
  mode: string;
  app_slug?: string;
}

interface AppInstallation {
  id: string;
  installation_id: number;
  account_login: string;
  account_type: string;
  created_at: string;
}

interface GitHubRepoLink {
  id: string;
  project_id: string;
  repo_owner: string;
  repo_name: string;
  is_primary: boolean;
  created_at: string;
}

interface RepoListResponse {
  count: number;
  repos: GitHubRepoLink[];
}

// FLOW-926: the meter's fill follows the server's `level`, never a client-side
// percentage — the same hues the `high`/`critical` badges wear, so bar and
// badge can never disagree.
const FILL_BY_LEVEL: Record<QuotaLevel, string> = {
  normal: "bg-signal-blue",
  warning: "bg-signal-amber",
  hard_stop: "bg-signal-red",
};

// FLOW-909: one unit for every estimate in the project. Set here and nowhere
// else — the detail rail renders it as a static suffix.
const ESTIMATE_UNIT_OPTIONS: SelectOption[] = [
  { value: "story_points", label: "Story points" },
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
  { value: "t_shirt_size", label: "T-shirt sizes" },
];

export const ProjectSettingsPage: Component = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();

  const projectId = (): ProjectId => params.id;
  const { showError, showSuccess } = useToast();
  const tooltips = useTooltips();
  const quota = useProjectQuota();

  const {
    data: project,
    loading,
    error,
    mutate,
  } = useApiWithSource(projectId, (id) => getProject(id));

  const [editName, setEditName] = createSignal("");
  const [editDescription, setEditDescription] = createSignal("");
  const [editBusinessPriority, setEditBusinessPriority] = createSignal("");
  const [editPrefix, setEditPrefix] = createSignal("");
  const [editEstimateUnit, setEditEstimateUnit] =
    createSignal<EstimateUnit>("story_points");
  const [saving, setSaving] = createSignal(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = createSignal(false);
  const [deleteConfirmName, setDeleteConfirmName] = createSignal("");
  const [deleting, setDeleting] = createSignal(false);

  const [showLeaveConfirm, setShowLeaveConfirm] = createSignal(false);
  const [leaving, setLeaving] = createSignal(false);

  const [archiving, setArchiving] = createSignal(false);
  const [archiveError, setArchiveError] = createSignal("");
  // Archiving deliberately does NOT `mutate` the project resource. The effect
  // below reloads members/repos/context and overwrites every edit field on any
  // change of the resource value, so routing the toggle through it would
  // silently discard a name the user had typed but not saved. `undefined`
  // means "no local change yet — trust the server value".
  const [archivedOverride, setArchivedOverride] = createSignal<
    string | null | undefined
  >(undefined);
  const archivedAt = (): string | null => {
    const local = archivedOverride();
    return local === undefined ? (project()?.archived_at ?? null) : local;
  };

  // `userRole()` is derived from `members()`, which starts empty — so "the
  // owner row has not arrived yet" and "the caller is not the owner" are the
  // same value. This says which one it is, so the read-only banner below can
  // stay quiet until the role actually resolves (FLOW-987).
  type RoleLoad = "pending" | "loaded" | "failed";
  const [roleLoad, setRoleLoad] = createSignal<RoleLoad>("pending");

  // The router reuses this component across `/projects/:id/settings` params,
  // so a local override must not survive into a different project. The member
  // rows go too: `userRole()` reads them, and the project resource keeps
  // serving project A's value until B's fetch lands, so leaving them would let
  // A's role gate B's controls for the width of that window (FLOW-987).
  //
  // `setMembers`/`setGuestLimit`/`setGuestCount` are declared below this
  // point, so this body only resolves because `createEffect` defers it past
  // the component body. Keep it a `createEffect`: `createComputed` or
  // `createRenderEffect` run eagerly and would throw on the temporal dead
  // zone instead.
  createEffect(() => {
    projectId();
    setArchivedOverride(undefined);
    setRoleLoad("pending");
    setMembers([]);
    setGuestLimit(null);
    setGuestCount(0);
  });

  const [githubStatus, setGithubStatus] = createSignal<GitHubStatus | null>(
    null,
  );
  const [installations, setInstallations] = createSignal<AppInstallation[]>([]);
  const [installationsLoading, setInstallationsLoading] = createSignal(false);
  const [removingInstallation, setRemovingInstallation] = createSignal<
    string | null
  >(null);

  const [repos, setRepos] = createSignal<GitHubRepoLink[]>([]);
  const [reposLoading, setReposLoading] = createSignal(false);
  const [linkOwner, setLinkOwner] = createSignal("");
  const [linkRepo, setLinkRepo] = createSignal("");
  const [linking, setLinking] = createSignal(false);
  const [linkError, setLinkError] = createSignal("");
  const [showManualRepoFallback, setShowManualRepoFallback] =
    createSignal(false);

  // Read from the store AppShell's auth gate publishes rather than issuing
  // another `GET /api/auth/me`: this page is a shell route, so the router
  // builds a fresh instance on every navigation to it and each own fetch cost
  // one request against the strict per-IP auth budget (FLOW-984).
  //
  // Accepted tradeoff: this page previously fetched its own user and so was
  // always fresh, whereas the store can be up to one throttle window stale.
  // That is deliberate even here, where the admin-gated controls are the most
  // sensitive in the app, because the role is a render hint only — every
  // mutation below re-resolves it server side via `require_project_tenant_admin`
  // (and `ActiveUser` re-checks `users.status`), so a stale role can surface a
  // control that then fails, never one that succeeds.
  const currentUserId = (): string | undefined => currentUser()?.id;
  const tenantMemberships = (): TenantMembership[] =>
    currentUser()?.tenants ?? [];
  // The role that matters here is the one in THIS project's tenant, not the
  // caller's primary tenant — `require_project_tenant_admin` resolves the
  // tenant from the project, and a user can be admin of one and member of
  // another.
  const currentTenantRole = (): TenantRole | null =>
    tenantRoleFor(tenantMemberships(), project()?.tenant_id);
  const userRole = (): ProjectRole | null =>
    getUserRole(currentUserId(), members());
  // "Manage" requires project ownership AND tenant admin; a real owner who
  // lacks org admin still needs `isOwner()` below to get the right banner copy.
  const isOwner = () => userRole() === "owner";
  const isProjectOwner = () =>
    canManageProject(userRole(), currentTenantRole());

  const [members, setMembers] = createSignal<ProjectMember[]>([]);
  const membersLoading = () => roleLoad() === "pending";
  // The caller's role is only known once the members list has landed AND the
  // auth store has published the caller — `getUserRole` matches on the id.
  const roleResolved = () => roleLoad() === "loaded" && currentUserId() != null;
  const roleUnavailable = () => roleLoad() === "failed";
  const [addUsername, setAddUsername] = createSignal("");
  const [adding, setAdding] = createSignal(false);
  const [memberError, setMemberError] = createSignal("");
  const [inviteEmail, setInviteEmail] = createSignal("");
  const [inviting, setInviting] = createSignal(false);
  const [inviteError, setInviteError] = createSignal("");
  const [outstandingInvites, setOutstandingInvites] = createSignal<
    PendingInvite[]
  >([]);
  const [invitesLoading, setInvitesLoading] = createSignal(false);
  const [revokingInviteId, setRevokingInviteId] = createSignal<string | null>(
    null,
  );
  const [changingTenantRoleFor, setChangingTenantRoleFor] = createSignal<
    string | null
  >(null);
  // FLOW-888: `guestLimit` is `null` on Pro (no cap enforced); the grandfather
  // notice below only applies once a prior downgrade left a project over a
  // newly-enforced Personal cap.
  const [guestLimit, setGuestLimit] = createSignal<number | null>(null);
  const [guestCount, setGuestCount] = createSignal(0);
  const [guestLimitOpen, setGuestLimitOpen] = createSignal(false);

  const [contextLoading, setContextLoading] = createSignal(false);
  const [contextGoals, setContextGoals] = createSignal("");
  const [contextConstraints, setContextConstraints] = createSignal("");
  const [contextTechStack, setContextTechStack] = createSignal("");
  const [contextCodebaseSummary, setContextCodebaseSummary] = createSignal("");
  const [savingContext, setSavingContext] = createSignal(false);

  createEffect(() => {
    const p = project();
    if (p) {
      setEditName(p.name);
      setEditDescription(p.description ?? "");
      setEditBusinessPriority(p.business_priority ?? "");
      setEditPrefix(p.prefix ?? "");
      setEditEstimateUnit(p.estimate_unit);
      loadRepos(p.id);
      loadMembers(p.id);
      loadGithubStatus();
      loadContext(p.id);
    }
  });

  // Load outstanding invites once we know both the project and the caller's
  // role — `canInviteMembers` needs `members()` and `tenantMemberships()`,
  // which resolve on their own async effects after `project()` does.
  createEffect(() => {
    const p = project();
    if (p && canInviteMembers(userRole(), currentTenantRole())) {
      loadInvitesList(p.id);
    }
  });

  const loadGithubStatus = async () => {
    try {
      const status = await api.get<GitHubStatus>("/github/status");
      setGithubStatus(status);
      if (status.configured && status.mode === "App (installation token)") {
        await loadInstallations();
      }
    } catch {
      // GitHub may not be configured; non-fatal
    }
  };

  const loadInstallations = async () => {
    setInstallationsLoading(true);
    try {
      const data = await api.get<{ installations: AppInstallation[] }>(
        "/github/app/installations",
      );
      setInstallations(data.installations);
    } catch {
      setInstallations([]);
    } finally {
      setInstallationsLoading(false);
    }
  };

  const handleInstallApp = () => {
    if (!githubStatus()?.app_slug) return;
    localStorage.setItem("github_app_return_url", window.location.pathname);
    // This install is not onboarding, and the callback returns here with
    // `?github_setup=success` just the same. An onboarding hop the user
    // abandoned leaves its marker behind indefinitely, and the shell's
    // resume arm (FLOW-991) would then swap this page for the wizard —
    // so drop any stale marker on the way out.
    clearOnboardingInFlight();
    // Redirect via the backend initiate endpoint (FLOW-501): the backend
    // issues an HttpOnly install_state nonce cookie and redirects to GitHub.
    window.location.href = "/api/github/app/install";
  };

  const handleRemoveInstallation = async (id: string) => {
    setRemovingInstallation(id);
    try {
      await api.delete(`/github/app/installations/${id}`);
      setInstallations((prev) => prev.filter((i) => i.id !== id));
      showSuccess("Installation removed");
    } catch (e) {
      showError(
        e instanceof Error ? e.message : "Failed to remove installation",
      );
    } finally {
      setRemovingInstallation(null);
    }
  };

  const loadRepos = async (id: string) => {
    setReposLoading(true);
    try {
      const data = await api.get<RepoListResponse>(
        `/projects/${id}/github/repos`,
      );
      setRepos(data.repos);
    } catch {
      // GitHub repos may not be configured; that's okay
      setRepos([]);
    } finally {
      setReposLoading(false);
    }
  };

  // Two loads are in flight whenever the caller switches projects, and nothing
  // orders the responses. Only the newest call may write: comparing ids alone
  // would still let a superseded call for the SAME project (A → B → A) land on
  // top of the newer one.
  let membersLoadGeneration = 0;

  const loadMembers = async (id: string) => {
    const generation = ++membersLoadGeneration;
    setRoleLoad("pending");
    try {
      const data = await listMembers(id);
      if (generation !== membersLoadGeneration) return;
      setMembers(data.members);
      setGuestLimit(data.guest_limit ?? null);
      setGuestCount(data.guest_count ?? 0);
      setRoleLoad("loaded");
    } catch {
      if (generation !== membersLoadGeneration) return;
      setMembers([]);
      setGuestLimit(null);
      setGuestCount(0);
      setRoleLoad("failed");
    }
  };

  const isGuestLimitError = (e: unknown): boolean =>
    e instanceof ApiError &&
    e.status === 409 &&
    (e.data as { code?: string } | null)?.code === "GUEST_LIMIT_REACHED";

  const handleAddMember = async () => {
    const username = addUsername().trim();
    if (!username) {
      setMemberError("GitHub username is required");
      return;
    }

    setAdding(true);
    setMemberError("");
    try {
      await addMember(projectId(), { github_username: username });
      setAddUsername("");
      await loadMembers(projectId());
      showSuccess("Member added");
    } catch (e) {
      if (isGuestLimitError(e)) {
        setGuestLimitOpen(true);
        return;
      }
      const message = e instanceof Error ? e.message : "Failed to add member";
      setMemberError(message);
      showError(message);
    } finally {
      setAdding(false);
    }
  };

  const handleSendInvite = async () => {
    const email = inviteEmail().trim();
    if (!email) {
      setInviteError("Email is required");
      return;
    }

    setInviting(true);
    setInviteError("");
    try {
      await sendInvite(projectId(), { email });
      setInviteEmail("");
      await loadInvitesList(projectId());
      showSuccess(`Invitation sent to ${email}`);
    } catch (e) {
      if (isGuestLimitError(e)) {
        setGuestLimitOpen(true);
        return;
      }
      const message = e instanceof Error ? e.message : "Failed to send invite";
      setInviteError(message);
      showError(message);
    } finally {
      setInviting(false);
    }
  };

  const handleViewPlansForGuestLimit = () => {
    setGuestLimitOpen(false);
    navigate("/settings/billing");
  };

  const loadInvitesList = async (id: ProjectId) => {
    setInvitesLoading(true);
    try {
      const data = await listInvites(id);
      // Either a rejected request or a resolved body with no `invites` key
      // shouldn't break the rest of the settings page — keep the signal an
      // array either way rather than trusting the response shape.
      setOutstandingInvites(data.invites ?? []);
    } catch {
      setOutstandingInvites([]);
    } finally {
      setInvitesLoading(false);
    }
  };

  const handleCancelInvite = async (invite: PendingInvite) => {
    setRevokingInviteId(invite.id);
    setInviteError("");
    try {
      await revokeInvite(projectId(), invite.id);
      await loadInvitesList(projectId());
      showSuccess("Invitation cancelled");
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to cancel invitation";
      setInviteError(message);
      showError(message);
    } finally {
      setRevokingInviteId(null);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await removeMember(projectId(), userId);
      await loadMembers(projectId());
      showSuccess("Member removed");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to remove member");
    }
  };

  const handleChangeTenantRole = async (userId: string, role: TenantRole) => {
    const tenantId = project()?.tenant_id;
    if (!tenantId) return;

    setChangingTenantRoleFor(userId);
    setMemberError("");
    try {
      await changeTenantMemberRole(tenantId, userId, role);
      await loadMembers(projectId());
      showSuccess(
        role === "admin"
          ? "Member promoted to admin"
          : "Admin demoted to member",
      );
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to change member's role";
      setMemberError(message);
      showError(message);
    } finally {
      setChangingTenantRoleFor(null);
    }
  };

  const handleSave = async () => {
    const p = project();
    if (!p) return;

    setSaving(true);
    try {
      const updated = await updateProject(p.id, {
        name: editName().trim() || undefined,
        description: editDescription().trim(),
        business_priority: editBusinessPriority().trim(),
        prefix: editPrefix().trim() || undefined,
        estimate_unit: editEstimateUnit(),
      });
      mutate(updated);
      showSuccess("Settings saved");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const p = project();
    if (!p) return;
    if (deleteConfirmName() !== p.name) return;

    setDeleting(true);
    try {
      await deleteProject(p.id);
      navigate("/projects", { replace: true });
    } catch (e) {
      setDeleting(false);
      showError(e instanceof Error ? e.message : "Failed to delete project");
    }
  };

  // Archiving frees an active-project slot; unarchiving claims one back and
  // is refused with 409 PROJECT_CAP_REACHED when the plan has none left
  // (FLOW-889). That message is the operator's instruction, so it is shown
  // inline next to the control rather than in a toast that scrolls away.
  const handleArchiveToggle = async () => {
    const p = project();
    if (!p) return;
    const wasArchived = archivedAt() !== null;

    setArchiving(true);
    setArchiveError("");
    try {
      const updated = wasArchived
        ? await unarchiveProject(p.id)
        : await archiveProject(p.id);
      setArchivedOverride(updated.archived_at);
      showSuccess(wasArchived ? "Project unarchived" : "Project archived");
    } catch (e) {
      setArchiveError(
        e instanceof Error
          ? e.message
          : `Failed to ${wasArchived ? "unarchive" : "archive"} project`,
      );
    } finally {
      setArchiving(false);
    }
  };

  const handleLeave = async () => {
    const uid = currentUserId();
    if (!uid) return;

    setLeaving(true);
    try {
      await removeMember(projectId(), uid);
      navigate("/projects", { replace: true });
    } catch (e) {
      setLeaving(false);
      showError(e instanceof Error ? e.message : "Failed to leave project");
    }
  };

  const handleResetTips = () => {
    tooltips.resetAll();
    showSuccess("Tips reset — they'll reappear on your next visit");
  };

  const loadContext = async (id: string) => {
    setContextLoading(true);
    try {
      const ctx = await getProjectContext(id);
      setContextGoals(ctx.goals.join("\n"));
      setContextConstraints(ctx.constraints.join("\n"));
      setContextTechStack(ctx.tech_stack.join(", "));
      setContextCodebaseSummary(ctx.codebase_summary);
    } catch {
      // Context may not exist yet; that's okay
    } finally {
      setContextLoading(false);
    }
  };

  const handleSaveContext = async () => {
    setSavingContext(true);
    try {
      const goals = contextGoals()
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const constraints = contextConstraints()
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const techStack = contextTechStack()
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      await updateProjectContext(projectId(), {
        goals,
        constraints,
        tech_stack: techStack,
        ...(repos().length === 0
          ? { codebase_summary: contextCodebaseSummary().trim() || undefined }
          : {}),
      });
      showSuccess("AI context saved");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to save AI context");
    } finally {
      setSavingContext(false);
    }
  };

  const handleLinkRepo = async () => {
    const owner = linkOwner().trim();
    const repo = linkRepo().trim();
    if (!owner || !repo) {
      setLinkError("Both owner and repository name are required");
      return;
    }

    setLinking(true);
    setLinkError("");
    try {
      const hasPrimary = repos().some((r) => r.is_primary);
      await api.post(`/projects/${projectId()}/github/repos`, {
        owner,
        repo,
        is_primary: !hasPrimary,
      });
      setLinkOwner("");
      setLinkRepo("");
      await loadRepos(projectId());
      showSuccess("Repository linked");
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to link repository";
      setLinkError(message);
      showError(message);
    } finally {
      setLinking(false);
    }
  };

  const handleUnlinkRepo = async (repoLinkId: string) => {
    try {
      await api.delete(`/github/repos/${repoLinkId}`);
      await loadRepos(projectId());
      showSuccess("Repository unlinked");
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to unlink repository");
    }
  };

  const handleSetPrimary = async (repoLinkId: string) => {
    try {
      await api.patch(`/github/repos/${repoLinkId}/primary`, {});
      await loadRepos(projectId());
      showSuccess("Primary repository updated");
    } catch (e) {
      showError(
        e instanceof Error ? e.message : "Failed to set primary repository",
      );
    }
  };

  return (
    <div class="h-full overflow-auto">
      <div class="max-w-2xl mx-auto px-6 py-8">
        {/* Loading */}
        <Show when={loading()}>
          <div class="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Show>

        {/* Error */}
        <Show when={error()}>
          <div class="bg-signal-red-tint border border-signal-red/25 rounded p-4 text-signal-red text-sm">
            Failed to load project. It may not exist or you may not have access.
          </div>
        </Show>

        {/* Project Settings */}
        <Show when={project()} keyed>
          {(p) => (
            <>
              {/* Breadcrumb */}
              <nav class="mb-6">
                <ol class="flex items-center gap-2 text-sm text-ink-3">
                  <li>
                    <A
                      href="/projects"
                      class="hover:text-ink transition-colors"
                    >
                      Projects
                    </A>
                  </li>
                  <li>/</li>
                  <li class="text-ink font-medium">{p.name}</li>
                </ol>
              </nav>

              <h1 class="text-2xl font-bold text-ink mb-8">Project Settings</h1>

              {/* Read-only banner for anyone who fails the owner+admin gate */}
              <Show when={roleResolved() && !isProjectOwner()}>
                <div class="mb-6 rounded border border-signal-amber/25 bg-signal-amber-tint px-4 py-3 text-sm text-signal-amber">
                  <Show
                    when={isOwner()}
                    fallback="Only the project owner can edit these settings."
                  >
                    You own this project, but changing its settings also
                    requires organization admin. Ask an admin in your
                    organization.
                  </Show>
                </div>
              </Show>

              {/* The role never resolved, so say that instead of asserting one */}
              <Show when={roleUnavailable()}>
                <div
                  role="alert"
                  class="mb-6 rounded border border-signal-red/25 bg-signal-red-tint px-4 py-3 text-sm text-signal-red"
                >
                  We couldn't load your role for this project, so these settings
                  stay read-only. Reload the page to try again.
                </div>
              </Show>

              {/* General Settings */}
              <section class="mb-8">
                <h2 class="text-lg font-semibold text-ink mb-4">General</h2>
                <div class="bg-surface rounded border border-line p-5 space-y-4">
                  <Input
                    label="Project Name"
                    value={editName()}
                    onInput={setEditName}
                    maxLength={255}
                    disabled={!isProjectOwner()}
                  />
                  <Input
                    label="Description"
                    value={editDescription()}
                    onInput={setEditDescription}
                    isTextarea
                    rows={3}
                    disabled={!isProjectOwner()}
                  />
                  <div>
                    <Input
                      label="Business Priority"
                      value={editBusinessPriority()}
                      onInput={setEditBusinessPriority}
                      isTextarea
                      rows={3}
                      maxLength={500}
                      placeholder="What is the team focused on right now?"
                      disabled={!isProjectOwner()}
                    />
                    <Show when={project()?.business_priority_updated_at}>
                      {(updatedAt) => (
                        <p class="text-xs text-ink-3 mt-1">
                          Last updated {new Date(updatedAt()).toLocaleString()}
                        </p>
                      )}
                    </Show>
                  </div>
                  <div>
                    <Input
                      label="Prefix"
                      value={editPrefix()}
                      onInput={setEditPrefix}
                      maxLength={10}
                      placeholder="Used for work item IDs (e.g. PRJ-42)"
                      disabled={!isProjectOwner()}
                    />
                    <p class="text-xs text-ink-3 mt-1">
                      Changing the prefix does not rename existing work items.
                    </p>
                  </div>
                  <div data-estimate-unit-setting>
                    <label class="block text-sm font-medium text-ink-2 mb-1">
                      Estimate unit
                    </label>
                    <Select
                      options={ESTIMATE_UNIT_OPTIONS}
                      value={editEstimateUnit()}
                      onChange={(v) => setEditEstimateUnit(v as EstimateUnit)}
                      disabled={!isProjectOwner()}
                    />
                    <p class="text-xs text-ink-3 mt-1">
                      Applies to every work item in this project. Changing it
                      relabels existing estimates; their values are kept.
                    </p>
                  </div>
                  <Show when={isProjectOwner()}>
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      loading={saving()}
                      disabled={saving()}
                    >
                      Save Changes
                    </Button>
                  </Show>
                </div>
              </section>

              {/* GitHub App Installations */}
              <Show
                when={
                  githubStatus()?.configured &&
                  githubStatus()?.mode === "App (installation token)"
                }
              >
                <section class="mb-8">
                  <h2 class="text-lg font-semibold text-ink mb-4">
                    GitHub App Installations
                  </h2>
                  <div class="bg-surface rounded border border-line p-5">
                    <p class="text-sm text-ink-3 mb-4">
                      Repositories are discovered from your GitHub App
                      installations. Install the App on additional accounts or
                      organisations to make their repositories available in the
                      picker.
                    </p>

                    <Show when={installationsLoading()}>
                      <div class="flex justify-center py-4">
                        <Spinner size="sm" />
                      </div>
                    </Show>

                    <Show when={!installationsLoading()}>
                      <Show
                        when={installations().length > 0}
                        fallback={
                          <p class="text-sm text-ink-3 mb-4">
                            No installations registered yet.
                          </p>
                        }
                      >
                        <ul class="space-y-2 mb-4">
                          <For each={installations()}>
                            {(inst) => (
                              <li class="flex items-center justify-between py-2 px-3 bg-sunken rounded">
                                <div class="flex items-center gap-2">
                                  <span class="text-sm font-medium text-ink">
                                    {inst.account_login}
                                  </span>
                                  <span class="text-xs bg-surface text-ink-2 border border-line px-1.5 py-0.5 rounded">
                                    {inst.account_type}
                                  </span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveInstallation(inst.id)
                                  }
                                  disabled={removingInstallation() === inst.id}
                                  loading={removingInstallation() === inst.id}
                                >
                                  Remove
                                </Button>
                              </li>
                            )}
                          </For>
                        </ul>
                      </Show>

                      <Show when={githubStatus()?.app_slug}>
                        <Button variant="secondary" onClick={handleInstallApp}>
                          Install on another account
                        </Button>
                      </Show>
                    </Show>
                  </div>
                </section>
              </Show>

              {/* GitHub Repositories */}
              <section class="mb-8">
                <h2 class="text-lg font-semibold text-ink mb-4">
                  GitHub Repositories
                </h2>
                <div class="bg-surface rounded border border-line p-5">
                  {/* Linked repos */}
                  <Show when={reposLoading()}>
                    <div class="flex justify-center py-4">
                      <Spinner size="sm" />
                    </div>
                  </Show>
                  <Show when={!reposLoading()}>
                    <Show
                      when={repos().length > 0}
                      fallback={
                        <p class="text-sm text-ink-3 mb-4">
                          <Show
                            when={isProjectOwner()}
                            fallback="Ask your project owner to link a repository to enable AI features."
                          >
                            Link a repository to get AI-powered architecture
                            analysis and code-aware suggestions.
                          </Show>
                        </p>
                      }
                    >
                      <ul class="space-y-3 mb-4">
                        <For each={repos()}>
                          {(repo) => (
                            <li class="flex items-center justify-between py-2 px-3 bg-sunken rounded">
                              <div>
                                <a
                                  href={`https://github.com/${repo.repo_owner}/${repo.repo_name}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="text-sm font-medium text-ink underline underline-offset-2 decoration-line-strong hover:decoration-ink"
                                >
                                  {repo.repo_owner}/{repo.repo_name}
                                </a>
                                <Show when={repo.is_primary}>
                                  <span class="ml-2 text-xs bg-surface text-ink-2 border border-line px-1.5 py-0.5 rounded">
                                    Primary
                                  </span>
                                </Show>
                              </div>
                              <Show when={isProjectOwner()}>
                                <div class="flex items-center gap-1">
                                  <Show when={!repo.is_primary}>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSetPrimary(repo.id)}
                                    >
                                      Set as primary
                                    </Button>
                                  </Show>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleUnlinkRepo(repo.id)}
                                  >
                                    Unlink
                                  </Button>
                                </div>
                              </Show>
                            </li>
                          )}
                        </For>
                      </ul>
                    </Show>

                    {/* Link new repo (owner only) */}
                    <Show when={isProjectOwner()}>
                      <div class="border-t border-line pt-4 mt-4">
                        <h3 class="text-sm font-medium text-ink-2 mb-3">
                          Link a Repository
                        </h3>
                        <Show
                          when={!showManualRepoFallback()}
                          fallback={
                            <div class="flex gap-2">
                              <Input
                                placeholder="owner"
                                value={linkOwner()}
                                onInput={setLinkOwner}
                                class="flex-1"
                              />
                              <span class="self-center text-ink-3">/</span>
                              <Input
                                placeholder="repo"
                                value={linkRepo()}
                                onInput={setLinkRepo}
                                class="flex-1"
                              />
                              <Button
                                variant="secondary"
                                onClick={handleLinkRepo}
                                loading={linking()}
                                disabled={linking()}
                              >
                                Link
                              </Button>
                            </div>
                          }
                        >
                          <RepoPicker
                            projectId={projectId()}
                            onSelect={(o, r) => {
                              setLinkOwner(o);
                              setLinkRepo(r);
                              handleLinkRepo();
                            }}
                            onFallback={() => setShowManualRepoFallback(true)}
                            onInstall={
                              githubStatus()?.mode ===
                                "App (installation token)" &&
                              githubStatus()?.app_slug
                                ? handleInstallApp
                                : undefined
                            }
                          />
                        </Show>
                        <Show when={linkError()}>
                          <p class="text-sm text-signal-red mt-2">
                            {linkError()}
                          </p>
                        </Show>
                      </div>
                    </Show>
                  </Show>
                </div>
              </section>

              {/* Repository Overview */}
              <Show when={repos().length > 0}>
                <RepositoryOverview
                  projectId={p.id}
                  repoId={(repos().find((r) => r.is_primary) ?? repos()[0]).id}
                  repoName={`${(repos().find((r) => r.is_primary) ?? repos()[0]).repo_owner}/${(repos().find((r) => r.is_primary) ?? repos()[0]).repo_name}`}
                />
              </Show>

              {/* AI Context */}
              <section class="mb-8">
                <h2 class="text-lg font-semibold text-ink mb-4">AI Context</h2>
                <div class="bg-surface rounded border border-line p-5 space-y-4">
                  <p class="text-sm text-ink-3">
                    Help the AI understand your project. This context is used to
                    make AI suggestions more relevant and specific to your work.
                    This context is sent to the AI when you use Chat and when
                    work items are enriched.
                  </p>

                  <Show when={contextLoading()}>
                    <div class="flex justify-center py-4">
                      <Spinner size="sm" />
                    </div>
                  </Show>

                  <Show when={!contextLoading()}>
                    <Input
                      label="Project Goals"
                      value={contextGoals()}
                      onInput={setContextGoals}
                      isTextarea
                      rows={3}
                      placeholder="One goal per line, e.g.&#10;Build a project management tool for engineering teams&#10;Integrate AI to automate routine PM tasks"
                      disabled={!isProjectOwner()}
                    />
                    <Input
                      label="Constraints & Non-goals"
                      value={contextConstraints()}
                      onInput={setContextConstraints}
                      isTextarea
                      rows={3}
                      placeholder="One constraint per line, e.g.&#10;Must work without AI services (graceful degradation)&#10;No mobile app in initial release"
                      disabled={!isProjectOwner()}
                    />
                    <Input
                      label="Tech Stack"
                      value={contextTechStack()}
                      onInput={setContextTechStack}
                      placeholder="Comma-separated, e.g. Rust, Axum, SolidJS, PostgreSQL, Tailwind"
                      disabled={!isProjectOwner()}
                    />
                    <Show
                      when={repos().length > 0}
                      fallback={
                        <div>
                          <Input
                            label="Codebase Overview"
                            value={contextCodebaseSummary()}
                            onInput={setContextCodebaseSummary}
                            isTextarea
                            rows={4}
                            placeholder="Brief description of the codebase architecture, key patterns, and conventions"
                            disabled={!isProjectOwner()}
                          />
                        </div>
                      }
                    >
                      <div>
                        <p class="text-sm font-medium text-ink-2 mb-1">
                          Codebase Overview
                        </p>
                        <p class="text-sm text-ink-3 bg-sunken rounded p-3 border border-line">
                          Codebase overview is generated and editable in the{" "}
                          <span class="font-medium">Repository Overview</span>{" "}
                          section above.
                        </p>
                      </div>
                    </Show>
                    <Show when={isProjectOwner()}>
                      <Button
                        variant="primary"
                        onClick={handleSaveContext}
                        loading={savingContext()}
                        disabled={savingContext()}
                      >
                        Save AI Context
                      </Button>
                    </Show>
                  </Show>
                </div>
              </section>

              {/* Members */}
              <section class="mb-8">
                <h2 class="text-lg font-semibold text-ink mb-4">Members</h2>
                <div class="bg-surface rounded border border-line p-5">
                  <Show when={membersLoading()}>
                    <div class="flex justify-center py-4">
                      <Spinner size="sm" />
                    </div>
                  </Show>
                  <Show when={roleUnavailable()}>
                    <p class="text-sm text-signal-red">
                      Couldn't load members.
                    </p>
                  </Show>
                  <Show when={!membersLoading() && !roleUnavailable()}>
                    <Show
                      when={members().length > 0}
                      fallback={
                        <p class="text-sm text-ink-3 mb-4">No members yet.</p>
                      }
                    >
                      <ul class="space-y-3 mb-4">
                        <For each={members()}>
                          {(member) => (
                            <li class="flex items-center justify-between py-2 px-3 bg-sunken rounded">
                              <div class="flex items-center gap-3">
                                <Avatar
                                  name={
                                    member.user_name ??
                                    member.github_username ??
                                    "?"
                                  }
                                  src={member.avatar_url}
                                  size="sm"
                                />
                                <div>
                                  <span class="text-sm font-medium text-ink">
                                    {member.user_name ?? member.github_username}
                                  </span>
                                  <Show when={member.github_username}>
                                    <span class="ml-1.5 text-xs text-ink-3">
                                      @{member.github_username}
                                    </span>
                                  </Show>
                                  <Show when={member.role === "owner"}>
                                    <span class="ml-2 text-xs bg-surface text-ink-2 border border-line px-1.5 py-0.5 rounded">
                                      Owner
                                    </span>
                                  </Show>
                                  <Show when={member.tenant_role === "admin"}>
                                    <span class="ml-2 text-xs bg-signal-green-tint text-signal-green px-1.5 py-0.5 rounded">
                                      Admin
                                    </span>
                                  </Show>
                                </div>
                              </div>
                              <div class="flex items-center gap-1">
                                <Show
                                  when={
                                    member.user_id === currentUserId() &&
                                    canLeaveProject(userRole())
                                  }
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowLeaveConfirm(true)}
                                  >
                                    Leave
                                  </Button>
                                </Show>
                                <Show
                                  when={
                                    member.user_id !== currentUserId() &&
                                    member.tenant_role != null &&
                                    canManageOrgRoles(currentTenantRole())
                                  }
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleChangeTenantRole(
                                        member.user_id,
                                        member.tenant_role === "admin"
                                          ? "member"
                                          : "admin",
                                      )
                                    }
                                    loading={
                                      changingTenantRoleFor() === member.user_id
                                    }
                                  >
                                    {member.tenant_role === "admin"
                                      ? "Make member"
                                      : "Make admin"}
                                  </Button>
                                </Show>
                                <Show
                                  when={
                                    member.user_id !== currentUserId() &&
                                    canManageMembers(
                                      userRole(),
                                      currentTenantRole(),
                                    ) &&
                                    member.role !== "owner"
                                  }
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleRemoveMember(member.user_id)
                                    }
                                  >
                                    Remove
                                  </Button>
                                </Show>
                              </div>
                            </li>
                          )}
                        </For>
                      </ul>
                    </Show>

                    {/* Grandfather notice (FLOW-888): a project that was
                        already over the guest cap when the tenant downgraded
                        to Personal keeps its existing collaborators — this is
                        informational, not an error. */}
                    <Show
                      when={
                        guestLimit() !== null &&
                        guestCount() > (guestLimit() ?? 0)
                      }
                    >
                      <p class="text-sm text-ink-2 bg-sunken rounded p-3 mb-4">
                        {"This project has " +
                          guestCount() +
                          " guest collaborators; the Personal plan includes " +
                          guestLimit() +
                          ". Existing collaborators keep access — upgrade to Pro to add more."}
                      </p>
                    </Show>

                    {/* Add member (owner only) */}
                    <Show
                      when={canManageMembers(userRole(), currentTenantRole())}
                    >
                      <div class="border-t border-line pt-4 mt-4">
                        <h3 class="text-sm font-medium text-ink-2 mb-3">
                          Add a Member
                        </h3>
                        <div class="flex gap-2">
                          <Input
                            placeholder="GitHub username"
                            value={addUsername()}
                            onInput={setAddUsername}
                            class="flex-1"
                          />
                          <Button
                            variant="secondary"
                            onClick={handleAddMember}
                            loading={adding()}
                            disabled={adding()}
                          >
                            Add
                          </Button>
                        </div>
                        <Show when={memberError()}>
                          <p class="text-sm text-signal-red mt-2">
                            {memberError()}
                          </p>
                        </Show>
                      </div>
                    </Show>

                    {/* Invite by email — owner AND tenant admin (FLOW-659 S2) */}
                    <Show
                      when={canInviteMembers(userRole(), currentTenantRole())}
                    >
                      <div class="border-t border-line pt-4 mt-4">
                        <h3 class="text-sm font-medium text-ink-2 mb-3">
                          Invite by Email
                        </h3>
                        <div class="flex gap-2">
                          <Input
                            type="email"
                            label="Email"
                            placeholder="teammate@example.com"
                            value={inviteEmail()}
                            onInput={setInviteEmail}
                            class="flex-1"
                          />
                          <Button
                            variant="secondary"
                            onClick={handleSendInvite}
                            loading={inviting()}
                            disabled={inviting()}
                          >
                            Send invite
                          </Button>
                        </div>
                        <Show when={inviteError()}>
                          <p class="text-sm text-signal-red mt-2">
                            {inviteError()}
                          </p>
                        </Show>

                        <div class="border-t border-line pt-4 mt-4">
                          <h4 class="text-sm font-medium text-ink-2 mb-3">
                            Outstanding Invitations
                          </h4>
                          <Show when={!invitesLoading()}>
                            <Show
                              when={outstandingInvites().length > 0}
                              fallback={
                                <p class="text-sm text-ink-3">
                                  No outstanding invitations.
                                </p>
                              }
                            >
                              <ul class="space-y-2">
                                <For each={outstandingInvites()}>
                                  {(invite) => (
                                    <li class="flex items-center justify-between py-2 px-3 bg-sunken rounded">
                                      <div>
                                        <span class="text-sm font-medium text-ink">
                                          {invite.email}
                                        </span>
                                        <Show when={invite.invited_by_name}>
                                          <span class="ml-1.5 text-xs text-ink-3">
                                            invited by {invite.invited_by_name}
                                          </span>
                                        </Show>
                                        <p class="text-xs text-ink-3">
                                          Expires{" "}
                                          {new Date(
                                            invite.expires_at,
                                          ).toLocaleString()}
                                        </p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        ariaLabel={`Cancel invitation for ${invite.email}`}
                                        onClick={() =>
                                          handleCancelInvite(invite)
                                        }
                                        loading={
                                          revokingInviteId() === invite.id
                                        }
                                        disabled={
                                          revokingInviteId() === invite.id
                                        }
                                      >
                                        Cancel
                                      </Button>
                                    </li>
                                  )}
                                </For>
                              </ul>
                            </Show>
                          </Show>
                        </div>
                      </div>
                    </Show>
                  </Show>
                </div>
              </section>

              {/* AI budget (FLOW-850 D10) */}
              <Show when={quota()}>
                {(q) => (
                  // `usage` is omitted by the server for anyone who may not
                  // see figures, so visibility is never a local role
                  // decision — key off its presence alone, never off a
                  // client-side role check.
                  <Show when={q().usage}>
                    {(usage) => {
                      // Floored so "100%" never shows beside "Near limit";
                      // clamped because the gate admits the call that
                      // pushes `used_tokens` past `budget_tokens`.
                      const meter = () => {
                        const budget = usage().budget_tokens;
                        if (budget === null || budget <= 0) return null;
                        const used = usage().used_tokens;
                        return {
                          pct: Math.min(100, Math.floor((used / budget) * 100)),
                          remaining: Math.max(budget - used, 0),
                          used,
                          budget,
                        };
                      };
                      return (
                        <section class="mb-8" data-testid="project-ai-budget">
                          <h2 class="text-lg font-semibold text-ink mb-4">
                            AI budget
                          </h2>
                          <div class="bg-surface rounded border border-line p-5">
                            <div class="flex flex-wrap items-center gap-3">
                              <Switch>
                                <Match when={usage().budget_tokens === null}>
                                  <p class="text-sm text-ink-3">
                                    This plan has no per-project budget; your
                                    organization&apos;s AI budget applies.
                                  </p>
                                </Match>
                                <Match when={usage().budget_tokens !== null}>
                                  <p class="text-sm text-ink-2">
                                    Used{" "}
                                    {usage().used_tokens.toLocaleString(
                                      "en-US",
                                    )}{" "}
                                    of{" "}
                                    {usage().budget_tokens?.toLocaleString(
                                      "en-US",
                                    )}{" "}
                                    tokens this month · resets{" "}
                                    {new Date(
                                      q().window_end,
                                    ).toLocaleDateString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </p>
                                </Match>
                              </Switch>
                              {/* Hoisted out of the Switch above so the badge
                              and link render independent of the budget
                              field — `level` is the project's level, and
                              the server reports `normal` for a null project
                              budget today. */}
                              <Show when={q().level === "warning"}>
                                <Badge variant="high">Near limit</Badge>
                              </Show>
                              <Show when={q().level === "hard_stop"}>
                                <Badge variant="critical">AI paused</Badge>
                                {/* `upgrade_url` is off-origin by construction
                                (config rejects anything but http(s)), so it
                                gets a plain anchor like `DegradedOutputBadge`
                                does; only the in-app fallback is a router
                                link. */}
                                <Show
                                  when={q().upgrade_url}
                                  fallback={
                                    <A
                                      href="/settings/billing"
                                      class="text-sm text-signal-blue underline"
                                    >
                                      Upgrade
                                    </A>
                                  }
                                >
                                  {(url) => (
                                    <a
                                      href={url()}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      class="text-sm text-signal-blue underline"
                                    >
                                      Upgrade
                                    </a>
                                  )}
                                </Show>
                              </Show>
                            </div>
                            <Show when={meter()}>
                              {(m) => (
                                <div class="mt-3">
                                  <div
                                    role="progressbar"
                                    aria-label="AI budget used this month"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                    aria-valuenow={m().pct}
                                    aria-valuetext={`${m().used.toLocaleString("en-US")} of ${m().budget.toLocaleString("en-US")} tokens used`}
                                    class="h-1.5 w-full rounded-full bg-line overflow-hidden"
                                  >
                                    <div
                                      class={`h-full rounded-full ${FILL_BY_LEVEL[q().level]}`}
                                      style={{ width: `${m().pct}%` }}
                                    />
                                  </div>
                                  <p class="mt-1.5 flex items-baseline justify-between gap-3 text-xs text-ink-3">
                                    <Show
                                      when={m().remaining > 0}
                                      fallback={
                                        <span>
                                          No tokens remaining this month
                                        </span>
                                      }
                                    >
                                      <span>
                                        {m().remaining.toLocaleString("en-US")}{" "}
                                        tokens remaining
                                      </span>
                                    </Show>
                                    <span class="font-mono tabular-nums text-ink">
                                      {m().pct}%
                                    </span>
                                  </p>
                                </div>
                              )}
                            </Show>
                          </div>
                        </section>
                      );
                    }}
                  </Show>
                )}
              </Show>

              {/* Preferences */}
              <section class="mb-8">
                <h2 class="text-lg font-semibold text-ink mb-4">Preferences</h2>
                <div class="bg-surface rounded border border-line p-5">
                  <div class="flex items-center justify-between">
                    <div class="flex-1">
                      <h3 class="text-sm font-medium text-ink mb-1">
                        Feature Discovery Tips
                      </h3>
                      <p class="text-sm text-ink-3">
                        Reset all dismissed tooltips to see helpful tips again
                        on your next visit to the board, backlog, and chat
                        views.
                      </p>
                    </div>
                    <Button variant="secondary" onClick={handleResetTips}>
                      Reset Tips
                    </Button>
                  </div>
                </div>
              </section>

              {/* Statuses & WIP limits (owner + tenant admin only) */}
              <StatusManager
                projectId={p.id}
                role={userRole}
                tenantRole={currentTenantRole}
              />

              {/* Danger Zone (owner only) */}
              <Show when={canDeleteProject(userRole(), currentTenantRole())}>
                <section>
                  <h2 class="text-lg font-semibold text-signal-red mb-4">
                    Danger Zone
                  </h2>
                  <div class="bg-surface rounded border border-signal-red/25 p-5 space-y-5">
                    <div class="flex items-center justify-between gap-4">
                      <div>
                        <h3 class="text-sm font-medium text-ink">
                          Archive project
                        </h3>
                        <p class="text-sm text-ink-3">
                          <Show
                            when={archivedAt()}
                            fallback="Make this project read-only and free an active project slot. Nothing is deleted."
                          >
                            This project is archived and read-only. Unarchiving
                            claims an active project slot.
                          </Show>
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        onClick={handleArchiveToggle}
                        loading={archiving()}
                        disabled={archiving()}
                      >
                        <Show when={archivedAt()} fallback="Archive">
                          Unarchive
                        </Show>
                      </Button>
                    </div>
                    <Show when={archiveError()}>
                      <p
                        role="alert"
                        class="text-sm text-signal-red bg-signal-red-tint border border-signal-red/25 rounded p-3"
                      >
                        {archiveError()}
                      </p>
                    </Show>

                    <div class="flex items-center justify-between gap-4 border-t border-line pt-5">
                      <div>
                        <h3 class="text-sm font-medium text-ink">
                          Delete Project
                        </h3>
                        <p class="text-sm text-ink-3">
                          Permanently delete this project and all its data.
                        </p>
                      </div>
                      <Button
                        variant="danger"
                        onClick={() => setShowDeleteConfirm(true)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </section>
              </Show>

              {/* Delete Confirmation Modal */}
              <Modal
                open={showDeleteConfirm()}
                onClose={() => {
                  setShowDeleteConfirm(false);
                  setDeleteConfirmName("");
                }}
                title="Delete Project"
                size="sm"
              >
                <div class="space-y-4">
                  <p class="text-sm text-ink-2">
                    This action cannot be undone. All work items, boards, and
                    settings will be permanently deleted.
                  </p>
                  <p class="text-sm text-ink-2">
                    Type{" "}
                    <strong class="font-mono text-signal-red">{p.name}</strong>{" "}
                    to confirm:
                  </p>
                  <Input
                    value={deleteConfirmName()}
                    onInput={setDeleteConfirmName}
                    placeholder={p.name}
                    error={
                      deleteConfirmName().length > 0 &&
                      deleteConfirmName() !== p.name
                        ? "Name doesn't match"
                        : undefined
                    }
                  />
                  <div class="flex justify-end gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmName("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleDelete}
                      loading={deleting()}
                      disabled={deleting() || deleteConfirmName() !== p.name}
                    >
                      Delete Project
                    </Button>
                  </div>
                </div>
              </Modal>
              {/* Leave Confirmation Modal */}
              <Modal
                open={showLeaveConfirm()}
                onClose={() => setShowLeaveConfirm(false)}
                title="Leave Project"
                size="sm"
              >
                <div class="space-y-4">
                  <p class="text-sm text-ink-2">
                    Are you sure you want to leave <strong>{p.name}</strong>?
                    You will lose access to this project and its work items.
                  </p>
                  <div class="flex justify-end gap-3">
                    <Button
                      variant="secondary"
                      onClick={() => setShowLeaveConfirm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleLeave}
                      loading={leaving()}
                      disabled={leaving()}
                    >
                      Leave Project
                    </Button>
                  </div>
                </div>
              </Modal>
              {/* Guest limit upgrade prompt (FLOW-888) */}
              <GuestLimitModal
                open={guestLimitOpen()}
                onClose={() => setGuestLimitOpen(false)}
                onViewPlans={handleViewPlansForGuestLimit}
              />
            </>
          )}
        </Show>
      </div>
    </div>
  );
};
